<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <div class="h-panel">
        <b-button
          v-if="mayManageTimetable"
          :to="{name: 'new_daily_quests_timetable_record'}"
          variant="primary">
          Schedule a quest
        </b-button>

        <b-button
          :disabled="isDeleteQuestsButtonDisabled"
          :variant="hasSelectedQuests ? 'danger' : 'light'"
          @click="deleteSelectedDailyQuestsWithConfirmation"
        >
          Delete
        </b-button>
      </div>

      <h2>Daily quests</h2>

      <p v-if="hasScheduleConflict" class="text-danger mb-3">
        {{scheduleConflictMessage}}
      </p>

      <table class="mb-2">
        <tr>
          <th class="pr-1">
            <label for="window-past-duration-selector" class="mb-0">Past</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="window-past-duration-selector"
              v-model="windowPastDuration"
              :options="windowDurationOptions"
            />
          </td>
          <th class="pr-1">
            <label for="window-future-duration-selector" class="mb-0">Future</label>
          </th>
          <td>
            <b-form-select
              id="window-future-duration-selector"
              v-model="windowFutureDuration"
              :options="windowDurationOptions"
            />
          </td>
        </tr>
      </table>

      <p v-if="message !== null" class="mt-3">
        {{message}}
      </p>

      <table v-if="hasQuestConts" class="table table-bordered text-nowrap">
        <thead class="thead-dark">
          <tr>
            <th>
              <b-form-checkbox :checked="allSelected" @change="allSelected = $event">Id</b-form-checkbox>
            </th>
            <th>Quest</th>
            <th>Audience</th>
            <th>Status</th>
            <th>Begin</th>
            <th>End</th>
            <th>Duration</th>
            <th>Start limit</th> <!-- Start available until -->
            <th>Min<br>player<br>level</th>
            <th>Min<br>client<br>version</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tr
          v-for="questCont in questConts"
          :key="questCont.quest.id"
          :class="getDailyQuestClass(questCont.quest)"
        >
          <td>
            <b-form-checkbox v-model="questCont.selected">
              {{questCont.quest.id}}
            </b-form-checkbox>
          </td>
          <td>
            <span
              v-b-popover.hover.top="getQuestProtoName(questCont.quest.quest_proto_id)"
            >
              {{questCont.quest.quest_proto_id | shortDailyQuestName}}
            </span>
          </td>
          <td>
            {{questCont.quest.for_qa | segmentName }}
          </td>
          <td :class="statusClass(questCont.quest)">
            {{questCont.quest | statusName}}
          </td>
          <td>
            {{questCont.quest.stime | getMomentMinStr}}
          </td>
          <td>
            {{questCont.quest | endAtStr}}
          </td>
          <td>
            {{questCont.quest.duration | getDurationStr}}
          </td>
          <td>
            {{questCont.quest | availableUntilStr}}
          </td>
          <td>
            {{questCont.quest.min_level}}
          </td>
          <td>
            {{questCont.quest.min_client_version | convertVersionCodeToStr}}
          </td>
          <td class="action-box">
            <b-button
              v-if="isEditButtonVisible(questCont.quest)"
              :to="{name: 'edit_daily_quests_timetable_record', params: {recordId: questCont.quest.id}}"
              size="sm"
              variant="success"
            >
              Edit
            </b-button>
            <b-button
              v-if="isDeleteButtonVisible"
              @click="deleteRecordWithConfirmation(questCont.quest)"
              variant="danger"
              size="sm"
            >
              Delete
            </b-button>
          </td>
        </tr>
      </table>

      <loading-indicator v-if="isLoading"/>

    </div>

  </div>
</template>

<script>
import auth from '../../auth.js';
import { HTTP } from '../../http-common.js';
import notifier from '../../notifier.js';
import { confirmAction } from '../../service/confirm.js';

import { DailyQuest           } from '../../model/daily_quests.js';
import { isDailyQuestEditable } from '../../model/daily_quests.js';

import { TIME_UNIT_MONTH          } from '../../model/event_time_filter.js';
import { getWindowDurationOptions } from '../../model/event_time_filter.js';
import { getBegin                 } from '../../model/event_time_filter.js';
import { getEnd                   } from '../../model/event_time_filter.js';

import { convertMomentToStamp    } from '../../util.js';
import { getMomentMinStr         } from '../../util.js';
import { getProtoAlias           } from '../../util.js';
import { isRoleAdmin             } from '../../util.js';
import { convertVersionCodeToStr } from '../../util.js';

import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'DailyQuestsTimetable',

  components: {
    MyHeader,
    LoadingIndicator,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      intersectingIds: null,

      // quest containers
      questConts: null,

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration:   DURATION_1_MONTH,
      windowFutureDuration: DURATION_1_MONTH,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {boolean} */
    hasScheduleConflict() {
      const intersectingIds = this.intersectingIds;
      if(intersectingIds === null) {
        return false;
      }
      return intersectingIds.length > 0;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && !this.hasQuestConts) {
        return 'No daily quests.';
      }

      return null;
    },

    /** @returns {boolean} */
    hasQuestConts() {
      const conts = this.questConts;
      if(conts === null) {
        return false;
      }

      return conts.length > 0;
    },

    /** @returns {boolean} */
    hasSelectedQuests() {
      const conts = this.questConts;
      if(conts === null) {
        return null;
      }

      for(const cont of conts) {
        if(cont.selected) {
          return true;
        }
      }
      return false;
    },

    selectedDailyQuestIds() {
      const conts = this.questConts;
      if(conts === null) {
        return null;
      }

      if(conts.length === 0) {
        return null;
      }

      const ids = [];

      for(const cont of conts) {
        if(cont.selected) {
          ids.push(cont.quest.id);
        }
      }

      return ids;
    },

    allSelected: {
      get() {
        for(const cont of this.questConts) {
          if(!cont.selected) {
            return false;
          }
        }
        return true;
      },

      set(newValue) {
        if(newValue) {
          this.selectAll();
        } else {
          this.deselectAll();
        }
      },
    },

    /** @returns {boolean} */
    isDeleteQuestsButtonDisabled() {
      return !this.hasSelectedQuests;
    },

    /** @returns {?string} */
    scheduleConflictMessage() {
      const intersectingIds = this.intersectingIds;
      if(intersectingIds === null) {
        return null;
      }
      if(intersectingIds.length === 0) {
        return null;
      }

      return 'Расписание ежедневных заданий составлено неправильно. Расписание содержит пересекающиеся ежедневные задания. Идентификаторы пересекающихся ежедневных заданий: ' + intersectingIds.join(', ') + '.';
    },

    /** @returns {boolean} */
    mayManageTimetable() {
      return isRoleAdmin(this.actor.role);
    },

    /** @returns {boolean} */
    isDeleteButtonVisible() {
      return isRoleAdmin(this.actor.role);
    },
  },

  created() {
    this.loadTimetable();
  },

  watch: {
    windowPastDuration() {
      this.loadTimetable();
    },

    windowFutureDuration() {
      this.loadTimetable();
    },
  },

  methods: {
    loadTimetable() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/daily_quests/timetable';

      const requestConf = {
        params: {}
      };

      const now = convertMomentToStamp(new Date());

      const windowPastDuration = this.windowPastDuration;
      if(windowPastDuration !== null) {
        requestConf.params.begin = getBegin(now, windowPastDuration);
      }

      const windowFutureDuration = this.windowFutureDuration;
      if(windowFutureDuration !== null) {
        requestConf.params.end = getEnd(now, windowFutureDuration);
      }

      HTTP.get(url, requestConf)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        if('intersecting' in data) {
          this.intersectingIds = data.intersecting;
        }

        const quests = data.timetable || [];

        this.questConts = [];
        for(const quest of quests) {
          const questCont = {
            selected: false,
            quest: quest,
          };
          this.questConts.push(questCont);
        }

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    statusClass(record) {
      const status = DailyQuest.getStatus(record);
      return {
        'status-active': DailyQuest.isActive(status),
      };
    },

    getDailyQuestClass(record) {
      const status = DailyQuest.getStatus(record);
      return {
        finished: DailyQuest.isFinished(status),
      };
    },

    getQuestProtoName(questProtoId) {
      return getProtoAlias(questProtoId);
    },

    isEditButtonVisible(quest) {
      if(!isRoleAdmin(this.actor.role)) {
        return false;
      }
      return isDailyQuestEditable(quest);
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectAll() {
      for(const container of this.questConts) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectAll() {
      for(const container of this.questConts) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    deleteRecordWithConfirmation(quest) {
      notifier.deleteSenderNotifications(this);

      this.confirmDailyQuestDeletion(
        quest.id,
        () => {
          this.deleteRecord(quest);
        }
      );
    },

    confirmDailyQuestDeletion(id, onConfirmed) {
      confirmAction(
        this,
        getDailyQuestDeletionConfirmationMessage(id),
        'Delete',
        onConfirmed,
      );
    },

    deleteRecord(record) {
      const url = `/daily_quests/timetable/${record.id}/delete`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        deleteQuestContFromArray(this.questConts, record.id);

        notifier.showSuccess('Daily quest deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    deleteSelectedDailyQuestsWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const ids = this.selectedDailyQuestIds;

      this.confirmDailyQuestsDeletion(
        ids,
        () => {
          this.deleteDailyQuests(ids);
        },
      );
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmDailyQuestsDeletion(ids, onConfirmed) {
      const params = {};
      if(ids.length > 1) {
        params.description = getDailyQuestIdsInfo(ids);
      }

      confirmAction(
        this,
        getDailyQuestsDeletionConfirmationMessage(ids),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} ids */
    deleteDailyQuests(ids) {
      const idsStr = ids.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/daily_quests/delete', data)
      .then(() => {
        deleteQuestContsFromArray(this.questConts, ids);

        const message = getDeletionCompleteMessage(ids.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },

  filters: {
    shortDailyQuestName: getShortDailyQuestName,

    convertVersionCodeToStr,
    endAtStr: function(r) {
      return getMomentMinStr(r.stime + r.duration);
    },
    availableUntilStr: function(r) {
      return getMomentMinStr(r.stime + r.duration - r.min_time_until_end_for_start);
    },
    statusName: function(r) {
      const status = DailyQuest.getStatus(r);
      return DailyQuest.getStatusName(status);
    },
    boolToStr: function(v) {
      return v ? 'yes' : 'no';
    },

    segmentName(forQA) {
      return forQA ? 'QA' : 'all';
    },
  },
};

function getShortDailyQuestName(protoId) {
  const alias = getProtoAlias(protoId);
  const PREFIX = '@daily_quests/quests/';
  if(alias.startsWith(PREFIX)) {
    return alias.substring(PREFIX.length);
  }
  return alias;
}

function deleteQuestContsFromArray(conts, ids) {
  for(const id of ids) {
    deleteQuestContFromArray(conts, id);
  }
}

function deleteQuestContFromArray(conts, id) {
  const index = findQuestContIndex(conts, id);
  if(index !== null) {
    conts.splice(index, 1);
  }
}

function findQuestContIndex(conts, id) {
  for(let i = 0; i < conts.length; i++) {
    const cont = conts[i];
    if(cont.quest.id === id) {
      return i;
    }
  }

  return null;
}

function getDailyQuestDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete daily quest ${id} {env}?`
}

function getDailyQuestsDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getDailyQuestDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} daily quests {env}?`;
}

function getDailyQuestIdsInfo(ids) {
  return 'Daily quest ids: ' + ids.join(', ') + '.';
}

function getDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Daily quest deleted.'
  } else {
    return 'Daily quests deleted.'
  }
}
</script>

<style scoped>
.finished {
  color: #dcdcdc;
}

.status-active {
  color: var(--green);
}

.action-box {
  padding: 7px;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
